import { useState, useRef, useEffect } from "react";
import "./pageHeader.scss";
import Search from "../Search/Search";
import { TbDots } from "react-icons/tb";

type Tab = {
    key?: string;
    label: React.ReactNode;
    count?: number;
    warn?: boolean;
    active: boolean;
    onClick: () => void;
};

type PageHeaderProps = {
    eyebrow?: string;
    /** UX comparison trial (option 3): small accent-colored icon next to the eyebrow label,
     *  a gentler spot for a page-identity icon than next to the (much larger) title. */
    eyebrowIcon?: React.ReactNode;
    eyebrowExtras?: string[];
    title?: string;
    /** Small icon shown inline before the title — same icon as this page's nav link, for
     *  visual continuity between the sidebar and the page itself. */
    titleIcon?: React.ReactNode;
    count?: string | number;
    lead?: React.ReactNode;
    loading?: boolean;
    tabs?: Tab[];
    search?: string;
    setSearch?: (value: string) => void;
    searchPlaceholder?: string;
    placeholder?: string;
    searchLabel?: string;
    primaryAction?: { label: React.ReactNode; onClick: () => void };
    secondaryAction?: React.ReactNode;
    overflowItems?: { label: React.ReactNode; onClick: () => void; danger?: boolean }[];
    subRightActions?: React.ReactNode;
    toolbarChildren?: React.ReactNode;
    accentColor?: string;
    introOnly?: boolean;
    sticky?: boolean;
    /** @deprecated Legacy roster header — use eyebrow/title/lead instead */
    Icon?: React.ComponentType<{ style?: React.CSSProperties }>;
    info?: string;
    infoActions?: React.ReactNode;
    buttonText?: React.ReactNode;
    setAction?: () => void;
    headerActions?: React.ReactNode;
};

function HeaderDoodle() {
    return (
        <svg
            className="page-header__doodle"
            viewBox="0 0 800 80"
            preserveAspectRatio="xMidYMid slice"
            xmlns="http://www.w3.org/2000/svg"
        >
            <circle cx="38" cy="40" r="26" fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.12" />
            <circle cx="38" cy="40" r="15" fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.09" />
            <circle cx="762" cy="40" r="26" fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.12" />
            <circle cx="762" cy="40" r="15" fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.09" />
            <circle cx="400" cy="-8" r="36" fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.07" />
            <circle cx="118" cy="16" r="2" fill="currentColor" opacity="0.12" />
            <circle cx="128" cy="24" r="1.4" fill="currentColor" opacity="0.09" />
            <circle cx="672" cy="60" r="2" fill="currentColor" opacity="0.12" />
            <circle cx="660" cy="52" r="1.4" fill="currentColor" opacity="0.09" />
            <circle cx="208" cy="65" r="1.6" fill="currentColor" opacity="0.1" />
            <circle cx="592" cy="14" r="1.6" fill="currentColor" opacity="0.1" />
            <line x1="102" y1="40" x2="113" y2="40" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.13" />
            <line x1="107" y1="35" x2="107" y2="46" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.13" />
            <line x1="687" y1="40" x2="698" y2="40" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.13" />
            <line x1="692" y1="35" x2="692" y2="46" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.13" />
            <path
                d="M 170,54 Q 230,32 290,52 Q 350,72 410,50 Q 470,28 530,50 Q 590,72 640,54"
                fill="none" stroke="currentColor" strokeWidth="0.7" strokeLinecap="round" opacity="0.08"
            />
            <rect x="157" y="16" width="8" height="8" rx="1.5" fill="none" stroke="currentColor" strokeWidth="0.9" opacity="0.12" />
            <rect x="635" y="56" width="8" height="8" rx="1.5" fill="none" stroke="currentColor" strokeWidth="0.9" opacity="0.12" />
        </svg>
    );
}

function LegacyPageHeader({
    Icon,
    title,
    info,
    infoActions,
    placeholder,
    buttonText,
    search,
    setSearch,
    setAction,
    headerActions,
    accentColor,
}: PageHeaderProps) {
    const UseIcon = Icon!;
    const hasSecondRow = !!(infoActions || headerActions);
    const accentStyle = accentColor ? ({ "--ph-accent": accentColor } as React.CSSProperties) : undefined;

    return (
        <div className="page-header page-header--legacy" style={accentStyle}>
            <HeaderDoodle />
            <div className="page-header__row page-header__row--main">
                <h1 className="page-header__title">
                    <UseIcon style={accentColor ? { color: accentColor } : undefined} />
                    <span className="page-header__title-text">{title}</span>
                    {info ? <span className="page-header__info">{info}</span> : null}
                </h1>
                <div className="header-actions">
                    {search !== undefined && setSearch && (
                        <Search value={search} onChange={setSearch} placeholder={placeholder ?? ""} />
                    )}
                    {buttonText && setAction ? (
                        <button className="btn-primary btn-table" onClick={setAction}>
                            {buttonText}
                        </button>
                    ) : null}
                </div>
            </div>
            {hasSecondRow && (
                <div className="page-header__row page-header__row--sub">
                    <div className="page-header__sub-left">{infoActions}</div>
                    <div className="page-header__sub-right">{headerActions}</div>
                </div>
            )}
        </div>
    );
}

export default function PageHeader(props: PageHeaderProps) {
    if (props.Icon) {
        return <LegacyPageHeader {...props} />;
    }

    const {
        eyebrow = "",
        eyebrowIcon,
        eyebrowExtras,
        title,
        titleIcon,
        count,
        lead,
        loading,
        tabs,
        search,
        setSearch,
        searchPlaceholder,
        searchLabel,
        placeholder,
        primaryAction,
        secondaryAction,
        overflowItems,
        subRightActions,
        toolbarChildren,
        accentColor,
        introOnly = false,
        sticky = false,
    } = props;

    const hasSub = !introOnly && !!(tabs?.length || subRightActions || toolbarChildren);
    const accentStyle = accentColor ? ({ "--ph-accent": accentColor } as React.CSSProperties) : undefined;
    const [kebabOpen, setKebabOpen] = useState(false);
    const [isStuck, setIsStuck] = useState(false);
    const kebabRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);

    // A sticky sibling further down (this page's own tabs section below, and/or a separate
    // <RosterSubBar>) hardcodes `top: 0` — but CSS sticky only accounts for a sibling's
    // *original* flow position, never a preceding sticky sibling's own stuck height, so that
    // only avoids overlapping this (also sticky) header for as long as the collapse-on-scroll
    // further below manages to shrink it in time. Not guaranteed — confirmed actually
    // overlapping on real data. Instead, measure this header's real height and publish it as
    // a CSS custom property on the shared parent (read by --sticky-header-h in
    // pageHeader.scss), so any sticky sibling can position itself exactly below it,
    // independent of scroll timing. Plain DOM, not a React prop, since the two elements
    // aren't parent/child — this is simpler and more robust than threading a value through
    // each page that uses this pattern.
    useEffect(() => {
        if (!sticky || !headerRef.current) return;
        const el = headerRef.current;
        const parent = el.parentElement;
        const report = () => {
            parent?.style.setProperty("--sticky-header-h", `${el.offsetHeight}px`);
        };
        const ro = new ResizeObserver(report);
        ro.observe(el);
        report();
        return () => ro.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sticky]);

    useEffect(() => {
        if (!kebabOpen) return;
        const handler = (e: MouseEvent) => {
            if (kebabRef.current && !kebabRef.current.contains(e.target as Node)) {
                setKebabOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [kebabOpen]);

    useEffect(() => {
        if (!sticky || !headerRef.current) return;
        // Walk up the DOM to find the nearest scrollable ancestor.
        // .roster-body (overflow-y: auto) is the scroll container, not the window.
        let scrollEl: HTMLElement | null = headerRef.current.parentElement;
        while (scrollEl) {
            const oy = window.getComputedStyle(scrollEl).overflowY;
            if (oy === "auto" || oy === "scroll") break;
            scrollEl = scrollEl.parentElement;
        }
        if (!scrollEl) return;
        const el = scrollEl;
        const isStuckRef = { current: false };
        const check = () => {
            const shouldStick = el.scrollTop > 0;
            if (shouldStick && !isStuckRef.current && headerRef.current) {
                // Collapsing hides eyebrow + lead, reducing header height by ~100px.
                // If that reduction would eliminate all remaining scroll room, skip the
                // collapse — otherwise scroll resets to 0 (visible as a page jump).
                const COMPACT_H = 56;
                const reduction = Math.max(0, headerRef.current.offsetHeight - COMPACT_H);
                const remainingScroll = el.scrollHeight - el.clientHeight - el.scrollTop;
                if (reduction > 0 && remainingScroll < reduction) return;
            }
            isStuckRef.current = shouldStick;
            setIsStuck(shouldStick);
        };
        el.addEventListener("scroll", check, { passive: true });
        check();
        return () => el.removeEventListener("scroll", check);
    }, [sticky]);

    return (
        <>
            <div
                ref={headerRef}
                className={[
                    "page-header",
                    sticky ? "page-header--sticky" : "",
                    isStuck ? "page-header--stuck" : "",
                ].filter(Boolean).join(" ")}
                style={accentStyle}
            >
                <div className="page-header__eyebrow">
                    <span className="page-header__eyebrow-dot" />
                    {eyebrowIcon && <span className="page-header__eyebrow-icon">{eyebrowIcon}</span>}
                    <span className="page-header__eyebrow-text">{eyebrow}</span>
                    {eyebrowExtras?.map((extra, i) => (
                        <span key={i} className="page-header__eyebrow-extra">
                            <span className="page-header__eyebrow-sep">·</span>
                            {extra}
                        </span>
                    ))}
                </div>

                <div className="page-header__main">
                    <h1 className="page-header__title">
                        {titleIcon && (
                            <span className="page-header__title-icon" style={accentColor ? { color: accentColor } : undefined}>
                                {titleIcon}
                            </span>
                        )}
                        {title ?? eyebrow}
                        {count !== undefined && (
                            <span className="page-header__title-count">{count}</span>
                        )}
                    </h1>
                    <div className="page-header__actions">
                        {searchLabel && <span className="page-header__search-label">{searchLabel}</span>}
                        {search !== undefined && setSearch && (
                            <Search
                                value={search}
                                onChange={setSearch}
                                placeholder={placeholder ?? searchPlaceholder}
                            />
                        )}
                        {secondaryAction}
                        {primaryAction && (
                            <button className="page-header__cta" onClick={primaryAction.onClick}>
                                {primaryAction.label}
                            </button>
                        )}
                        {overflowItems && overflowItems.length > 0 && (
                            <div className="page-header__kebab-wrap" ref={kebabRef}>
                                <button
                                    className="page-header__kebab"
                                    onClick={() => setKebabOpen((o) => !o)}
                                    title="More"
                                >
                                    <TbDots />
                                </button>
                                {kebabOpen && (
                                    <div className="page-header__kebab-menu">
                                        {overflowItems.map((item, i) => (
                                            <button
                                                key={i}
                                                className={`page-header__kebab-item${item.danger ? " page-header__kebab-item--danger" : ""}`}
                                                onClick={() => {
                                                    item.onClick();
                                                    setKebabOpen(false);
                                                }}
                                            >
                                                {item.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {loading && !lead
                    ? <span className="page-header__lead-skeleton" />
                    : lead && <p className="page-header__lead">{lead}</p>
                }
            </div>

            {hasSub && (
                <div className="roster-sticky-toolbar" style={accentStyle}>
                    <div className="page-header__sub">
                        <div className="page-header__tabs-scroller">
                            {tabs && (
                                <div className="page-header__tabs">
                                    {tabs.map((tab, i) => (
                                        <button
                                            key={tab.key ?? i}
                                            className={[
                                                "page-header__tab",
                                                tab.active ? "page-header__tab--active" : "",
                                                tab.warn ? "page-header__tab--warn" : "",
                                            ]
                                                .filter(Boolean)
                                                .join(" ")}
                                            onClick={tab.onClick}
                                        >
                                            {tab.label}
                                            {tab.count !== undefined && (
                                                <span className="page-header__tab-count">{tab.count}</span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        {subRightActions && (
                            <div className="page-header__sub-right">{subRightActions}</div>
                        )}
                    </div>
                    {toolbarChildren}
                </div>
            )}
        </>
    );
}
